import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, User, Hash, MapPin } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { trackEvent } from "../lib/analytics";
import useAnonymousSearchTracker from "../lib/useAnonymousSearchTracker";
import AnonymousSignupGate from "./AnonymousSignupGate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "District of Columbia" }
];

const SearchBar = ({ initialTab = "name", initialValues = {} }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Name search
  const [firstName, setFirstName] = useState(initialValues.firstName || "");
  const [lastName, setLastName] = useState(initialValues.lastName || "");
  
  // NPI search
  const [npiNumber, setNpiNumber] = useState(initialValues.npiNumber || "");
  
  // Specialty + Location search
  const [specialty, setSpecialty] = useState(initialValues.specialty || "");
  const [state, setState] = useState(initialValues.state || "");
  const [city, setCity] = useState(initialValues.city || "");

  // Anonymous search tracking (no-op for authenticated users)
  const { trackSearch, gateOpen, closeGate } = useAnonymousSearchTracker();

  const handleSearch = async (e) => {
    e.preventDefault();

    // Gate anonymous users after 3 searches — backend increments + returns reached_limit
    const tracking = await trackSearch();
    if (tracking?.reached_limit) {
      // Modal is already opened by the tracker hook; block navigation.
      return;
    }

    if (activeTab === "name") {
      const nameParts = [];
      if (firstName) nameParts.push(firstName.toLowerCase().trim());
      if (lastName) nameParts.push(lastName.toLowerCase().trim());
      if (nameParts.length > 0) {
        const nameSlug = nameParts.join('-').replace(/[^a-z0-9-]/g, '');
        trackEvent("search_submit", { search_type: "name", query: nameParts.join(" ") });
        navigate(`/search/name/${nameSlug}`);
      }
    } else if (activeTab === "npi") {
      if (npiNumber && npiNumber.length === 10) {
        trackEvent("search_submit", { search_type: "npi", query: npiNumber });
        navigate(`/search/npi/${npiNumber}`);
      }
    } else if (activeTab === "specialty") {
      if (specialty && state) {
        const specSlug = specialty.toLowerCase().replace(/\s+/g, '-');
        trackEvent("search_submit", { search_type: "specialty_state", specialty, state });
        navigate(`/specialty/${specSlug}/${state.toLowerCase()}`);
      } else if (specialty) {
        const specSlug = specialty.toLowerCase().replace(/\s+/g, '-');
        trackEvent("search_submit", { search_type: "specialty", specialty });
        navigate(`/specialty/${specSlug}`);
      } else if (state) {
        trackEvent("search_submit", { search_type: "state", state });
        navigate(`/state/${state}`);
      }
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <AnonymousSignupGate open={gateOpen} onClose={closeGate} />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-3 mb-6 bg-slate-100 p-1 rounded-lg">
          <TabsTrigger 
            value="name" 
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-navy data-[state=active]:shadow-sm rounded-md"
            data-testid="tab-search-by-name"
          >
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Search by Name</span>
            <span className="sm:hidden">Name</span>
          </TabsTrigger>
          <TabsTrigger 
            value="npi"
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-navy data-[state=active]:shadow-sm rounded-md"
            data-testid="tab-search-by-npi"
          >
            <Hash className="w-4 h-4" />
            <span className="hidden sm:inline">Search by NPI</span>
            <span className="sm:hidden">NPI</span>
          </TabsTrigger>
          <TabsTrigger 
            value="specialty"
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-navy data-[state=active]:shadow-sm rounded-md"
            data-testid="tab-search-by-specialty"
          >
            <MapPin className="w-4 h-4" />
            <span className="hidden sm:inline">Specialty + Location</span>
            <span className="sm:hidden">Location</span>
          </TabsTrigger>
        </TabsList>

        <form onSubmit={handleSearch}>
          <TabsContent value="name" className="mt-0">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="flex-1 h-14 text-lg px-6 border-slate-200 focus:ring-2 focus:ring-teal focus:border-teal"
                data-testid="input-first-name"
              />
              <Input
                type="text"
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="flex-1 h-14 text-lg px-6 border-slate-200 focus:ring-2 focus:ring-teal focus:border-teal"
                data-testid="input-last-name"
              />
              <Button 
                type="submit"
                className="h-14 px-8 bg-navy hover:bg-navy/90 text-white font-medium rounded-lg"
                data-testid="search-submit-button"
              >
                <MagnifyingGlass className="w-5 h-5 mr-2" />
                Search
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="npi" className="mt-0">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="text"
                placeholder="Enter 10-digit NPI number"
                value={npiNumber}
                onChange={(e) => setNpiNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="flex-1 h-14 text-lg px-6 font-mono border-slate-200 focus:ring-2 focus:ring-teal focus:border-teal"
                maxLength={10}
                data-testid="input-npi-number"
              />
              <Button 
                type="submit"
                className="h-14 px-8 bg-navy hover:bg-navy/90 text-white font-medium rounded-lg"
                data-testid="search-submit-button"
              >
                <MagnifyingGlass className="w-5 h-5 mr-2" />
                Search
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="specialty" className="mt-0">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="text"
                  placeholder="Specialty (e.g., Cardiology, Pediatrics)"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  className="flex-1 h-14 text-lg px-6 border-slate-200 focus:ring-2 focus:ring-teal focus:border-teal"
                  data-testid="input-specialty"
                />
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger 
                    className="w-full sm:w-48 h-14 text-lg px-6 border-slate-200"
                    data-testid="select-state"
                  >
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s.code} value={s.code}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="text"
                  placeholder="City (optional)"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="flex-1 h-14 text-lg px-6 border-slate-200 focus:ring-2 focus:ring-teal focus:border-teal"
                  data-testid="input-city"
                />
                <Button 
                  type="submit"
                  className="h-14 px-8 bg-navy hover:bg-navy/90 text-white font-medium rounded-lg"
                  data-testid="search-submit-button"
                >
                  <MagnifyingGlass className="w-5 h-5 mr-2" />
                  Search
                </Button>
              </div>
            </div>
          </TabsContent>
        </form>
      </Tabs>
    </div>
  );
};

export default SearchBar;
